<template>
  <div :class="classes">
    <m4-label v-if="computedLabel" :error="!!error" class="form-field__label">
      {{ computedLabel }}
    </m4-label>
    <component ref="form_field" :is="controlTag" v-bind="controlProps" :value="newValue" class="form-field__control">
      <slot></slot><template v-if="slotSuffix"> {{ slotSuffix }}</template>
    </component>
    <m4-hint v-if="error || customError" class="form-field__error-hint" variant="error">
      {{ customError ? customError : error }}
    </m4-hint>
    <m4-hint v-else-if="hint" class="form-field__hint">{{ hint }}</m4-hint>
    <m4-button
      v-if="controlOptions.generatePassword !== undefined && controlOptions.generatePassword"
      @click="genPassword()"
      tag="a"
      variant="primary-text"
      class="form-field__right-item"
    >
      <small>Generate password</small>
    </m4-button>
    <m4-button
      v-if="controlOptions.rightLinkUrl !== undefined && controlOptions.rightLinkUrl"
      :href="controlOptions.rightLinkUrl"
      tag="a"
      variant="primary-text"
      class="form-field__right-item"
    >
      <small>{{ controlOptions.rightLinkTitle }}</small>
    </m4-button>
    <m4-button
      v-if="controlOptions.showPasswordIcon !== undefined && controlOptions.showPasswordIcon"
      tag="a"
      variant="default-text"
      class="form-field__right-item"
      @click="toggle = !toggle"
    >
      <m4-icon v-if="toggle" icon="visible"></m4-icon>
      <m4-icon v-else icon="invisible"></m4-icon>
    </m4-button>
  </div>
</template>

<script>
import valueProp from "../../../utils/valueProp.js";

export default {
  name: "M4FormField",
  mixins: [valueProp],
  data() {
    let disVal = this.$attrs.disabled != undefined;

    if (this.reactiveDisabled) {
      disVal = this.reactiveDisabled;
    }

    let value = this.modelValue ? this.modelValue : this.value;

    if (!value && this.type !== "checkbox") {
      value = null;
    }

    return {
      newDisabled: disVal,
      display: true,
      newValue: value,
      customError: null,
      toggle: false
    };
  },
  props: {
    label: {
      type: String
    },
    hint: {
      type: String
    },
    error: {
      type: String
    },
    modelValue: {
      type: [String, Number, Boolean, Array, Event, Object],
      default: undefined
    },
    type: {
      type: String,
      default: "text"
    },
    required: {
      type: Boolean
    },
    optional: {
      type: Boolean
    },
    controlOptions: {
      type: Object,
      default() {
        return {};
      }
    },
    reactiveDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["initformfield"],
  mounted: function () {
    this.$nextTick(function () {
      this.$emit("initformfield", this.value);
    });
  },
  methods: {
    applySuffixToTitle(title, force = false) {
      let text = [title];
      if ((title || force) && this.required) {
        text.push("(" + this.$t("required") + ")");
      }
      if ((title || force) && this.optional) {
        text.push("(optional)");
      }
      return text.join(" ");
    },
    setDisabled(dis) {
      this.newDisabled = dis;
    },
    setDisplay(bool) {
      this.display = bool;
    },
    setNewValue(value) {
      this.newValue = value;
      if (this.type == "country-field") {
        this.$refs.form_field.setCountry(this.newValue.country);
        if (this.newValue.state && this.newValue.state != undefined) {
          this.$refs.form_field.setState(this.newValue.state);
        }
      } else if (this.type == "select-custom-text") {
        this.$refs.form_field.setNewValue(this.newValue);
      } else if (this.type == "select") {
        this.$refs.form_field.setSelected(this.newValue);
      } else if (this.type == "number") {
        this.$refs.form_field.setNewValue(this.newValue);
      }
    },
    getNewValue() {
      return this.newValue;
    },
    setCustomError(error) {
      this.customError = error;
    },
    genPassword() {
      let result = " ";
      const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      const charactersLength = characters.length;
      for (let i = 0; i < 20; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
      }

      this.newValue = result;
    }
  },
  computed: {
    classes() {
      let classes = ["form-field"];
      if (this.type) {
        classes.push("form-field--" + this.type);
      }
      if (this.$attrs.placeholder) {
        classes.push("form-field--has-placeholder");
      }
      if (!this.display) {
        classes.push("form-field--hidden");
      }
      if (this.controlOptions.generatePassword !== undefined && this.controlOptions.generatePassword) {
        classes.push("form-field--generate-password");
      }
      if (this.controlOptions.rightLinkUrl !== undefined && this.controlOptions.rightLinkUrl) {
        classes.push("form-field--right-link-url");
      }
      if (this.controlOptions.showPasswordIcon !== undefined && this.controlOptions.showPasswordIcon) {
        classes.push("form-field--show-password-icon");
      }

      return classes;
    },
    computedLabel() {
      return this.label ? this.applySuffixToTitle(this.label) : null;
    },
    controlTag() {
      if (this.type == "select") {
        return "m4-select";
      } else if (this.type == "select-custom-text") {
        return "m4-select-custom-text";
      } else if (this.type == "multiselect") {
        return "m4-multiselect";
      } else if (this.type == "checkbox") {
        return "m4-checkbox";
      } else if (this.type == "checkbox-group") {
        return "m4-checkbox-group";
      } else if (this.type == "radio-group") {
        return "m4-radio-group";
      } else if (this.type == "file-input") {
        return "m4-file-input";
      } else if (this.type == "upload-field") {
        return "m4-upload-field";
      } else if (this.type == "multi-upload-field") {
        return "m4-multi-upload-field";
      } else if (this.type == "country-field") {
        return "m4-country-field";
      } else if (this.type == "select2") {
        return "m4-select2";
      } else if (this.type == "datetime-picker") {
        return "m4-datetime-picker";
      } else if (this.type == "ace-editor") {
        return "m4-ace-editor";
      } else if (this.type == "select2-group") {
        return "m4-select2-group";
      } else if (this.type == "html-editor") {
        return "m4-html-editor";
      } else if (this.type == "title-value-field-group") {
        return "m4-title-value-field-group";
      } else if (this.type == "select-value-field-group") {
        return "m4-select-value-field-group";
      } else if (this.type == "value-field-group") {
        return "m4-value-field-group";
      } else if (this.type == "switch") {
        return "m4-switch";
      } else if (this.type == "number") {
        return "m4-numeric-field";
      } else if (this.type == "iban") {
        return "m4-iban-field";
      } else if (this.type == "select-union") {
        return "m4-select-union";
      } else if (this.type == "radio-checkbox-group") {
        return "m4-radio-checkbox-group";
      } else if (this.type == "permission-list") {
        return "m4-permission-list";
      } else if (this.type == "tag-group-field") {
        return "m4-tag-group-field";
      } else if (this.type == "note") {
        return "m4-note-field";
      }
      return "m4-input";
    },
    slotSuffix() {
      if (this.type == "checkbox" && !this.label) {
        return this.applySuffixToTitle("", true);
      }
      return null;
    },
    controlProps() {
      let type = null;
      let allowTypeFor = ["checkbox", "number", "text", "textarea", "password", "email"];

      if (allowTypeFor.indexOf(this.type) > -1 || !this.type) {
        type = this.type;
      }

      if (
        this.type == "password" &&
        this.controlOptions.generatePassword !== undefined &&
        this.controlOptions.generatePassword
      ) {
        type = "text";
      }

      if (
        this.type == "password" &&
        this.controlOptions.showPasswordIcon !== undefined &&
        this.controlOptions.showPasswordIcon &&
        this.toggle
      ) {
        type = "text";
      }

      let defObj = Object.assign(
        {
          value: this.newValue,
          type: type,
          error: !!this.error || !!this.customError
        },
        this.controlOptions,
        this.$attrs,
        this.newDisabled
          ? {
              disabled: this.newDisabled
            }
          : {},
        this.$attrs.placeholder ? { placeholder: this.applySuffixToTitle(this.$attrs.placeholder) } : null
      );

      if (!this.newDisabled) {
        delete defObj.disabled;
        defObj = Object.assign({}, defObj);
      }

      return defObj;
    }
  },
  watch: {
    value(value) {
      this.newValue = value;
    },
    modelValue(value) {
      this.newValue = value;
    },
    reactiveDisabled(val) {
      if (this.reactiveDisabled !== this.newDisabled) {
        this.setDisabled(val);
      }
    }
  }
};
</script>
